/**
 * The 3D world for a square window of tiles: the columns of the plateau as one geometry, the
 * models placed on it as instances per model, and the markers. What stands where comes from the
 * shared placement rules (placement.ts); offsets in art units become tile
 * offsets. Decision 0009.
 */
import { mix32, Terrain } from "@mistsweeper/core";
import {
  bearsObjects,
  edgeDetailOn,
  fishOn,
  ghostsOn,
  HOME,
  hasTop as hasTopAt,
  makePick,
  objectsOn,
  type Pick,
  type Placement,
  SWAY,
  type Sway,
  shown,
  struck,
  swarmOn,
  WATER_DROP,
} from "./placement.ts";
import { WindowWorld } from "./window-world.ts";
import type { WorldSource } from "./world-source.ts";

/** How many tiles the columns reach down into the mist. Visual spec, Geometry. */
export const DEPTH = 1.8;

const SALT_VARIANT = 0x76617231; // "var1"
/**
 * How far round its window a scene reads the world at once: past the farthest any placement rule
 * looks from a tile, the frontier's three, a block's corner and a landmark's region. Farther, the
 * world answers itself.
 */
const WINDOW_MARGIN = 8;
/** The banner (decision 0011): the flag, turned to the camera, and its cairn on shown ground. */
export const BANNER_FLAG = "iso-banner-1-flag";
const BANNER_CAIRN = "iso-banner-1-cairn";

/** Instance flags, read by the shaders. */
export const FLAG = {
  /**
   * A silhouette in the mist: a flat tone, no outline, no reveal fade, no shadow, and its foot
   * drawn down to the mist floor so it rises out of the mist.
   */
  ghost: 1,
  /** Ink parts are drawn in the accent role: the chasm that ended the run. */
  accent: 2,
  /** The mist has lifted here whatever the height: chasm floors. */
  clear: 4,
  /** Turns around its own x axis through its origin: a windmill's sails. */
  spin: 8,
  /** Nods around its own z axis through its origin, down to graze and up: a deer's head. */
  nod: 16,
  /** Stands clear of its tile's mist, its weight 0, so only its height fades it: a banner. */
  aloft: 32,
  /** Turns about its foot to face the camera, built facing the default view: a banner. */
  face: 64,
  /** Below the plateau's top it fades into the mist: a creature rising out of its gap. */
  misted: 128,
  /**
   * Turns about its foot to face the camera, as a banner does, and its accent parts sway a little
   * below its cloth probe: the home cabin's flagpole and its pennant.
   */
  pennant: 256,
  /** Stands on a block of 2 by 2 tiles from its own, and takes the largest of their mist: a massif. */
  wide: 512,
  /** Circles slowly about its own vertical axis through its origin: an eagle soaring. */
  soar: 1024,
  /** Swims round its origin, looks about and upends to feed: a duck (the model shader). */
  dabble: 2048,
  /** A puff of smoke: rises from its origin, grows, drifts with the wind and dissolves. */
  smoke: 4096,
} as const;

/**
 * The motions a model names for itself in the model file (decision 0019), as the flags above:
 * the custom property `motion` of its collection in world.blend.
 */
export const MOTION_FLAGS: Readonly<Record<string, number>> = {
  spin: FLAG.spin,
  nod: FLAG.nod,
  pennant: FLAG.pennant,
  soar: FLAG.soar,
  dabble: FLAG.dabble,
};

/** A model that rides on another one: placed at a point of its host, turned and sized with it. */
export interface Attachment {
  readonly name: string;
  /** Where on the host, in the host model's own space, in tiles. */
  readonly x: number;
  readonly y: number;
  readonly z: number;
  readonly flags: number;
}

/** Where smoke rises from a model, in its own space, in tiles, and how large its puffs are. */
export interface SmokeSource {
  readonly x: number;
  readonly y: number;
  readonly z: number;
  readonly size: number;
}

/** The model of a puff of smoke, and how many rise from a source at a time. */
export const PUFF = "iso-puff-1";
export const PUFFS_3D = 5;

export interface Instance {
  readonly x: number;
  readonly y: number;
  /** Height of its foot: 0 on the plateau's top, below it on water. */
  readonly h: number;
  /** The tile the instance belongs to, whose mist weight sinks it. */
  readonly tx: number;
  readonly ty: number;
  readonly flags: number;
  /** Turn around the vertical in radians, and size. */
  readonly yaw: number;
  readonly scale: number;
  /** How it moves in the wind: 0 not at all, 1 like a tree, 2 like grass. */
  readonly sway: Sway;
  /** Its place among the instances of its tile, so two of the same model get their own outlines. */
  readonly slot: number;
  /** A puff of smoke: its number among its source's puffs, and the source's size. */
  readonly puff?: { readonly k: number; readonly size: number };
}

export interface Marker {
  readonly kind: "digit" | "banner" | "wrong";
  readonly x: number;
  readonly y: number;
  readonly n: number;
}

/** A swarm of gnats: its centre in the world, its tile and how many dance in it. */
export interface Gnats {
  readonly x: number;
  readonly y: number;
  readonly h: number;
  readonly tx: number;
  readonly ty: number;
  readonly n: number;
}

/** A fish that leaps now and then: its place on the water, its tile, which way and how often. */
export interface Fish {
  readonly x: number;
  readonly y: number;
  /** The water's level. */
  readonly h: number;
  readonly tx: number;
  readonly ty: number;
  readonly heading: number;
  readonly period: number;
  readonly phase: number;
}

/**
 * A duck on the water, for the rings that spread from it: the place it swims round, the water's
 * level and its size.
 */
export interface Duck {
  readonly x: number;
  readonly y: number;
  readonly h: number;
  readonly scale: number;
  readonly tx: number;
  readonly ty: number;
}

export interface Scene {
  /** Tile at the window's corner; the window is `size` tiles square. */
  readonly x0: number;
  readonly y0: number;
  readonly size: number;
  /** Columns: positions, flat tones, ids and the window-relative tile per vertex. */
  readonly columns: {
    readonly position: Float32Array;
    readonly tone: Float32Array;
    readonly id: Float32Array;
    readonly tile: Float32Array;
    /** 1 on a water surface, which ripples; 0 elsewhere. */
    readonly kind: Float32Array;
    readonly count: number;
  };
  /**
   * The ground of each tile of the window, row by row: 0 without a top, else its terrain plus 1
   * (water 1, grass 2, forest 3, mountain 4), plus 8 where a number shows. Grass on the tops.
   */
  readonly ground: Uint8Array;
  readonly instances: ReadonlyMap<string, Instance[]>;
  readonly markers: readonly Marker[];
  readonly swarms: readonly Gnats[];
  readonly fish: readonly Fish[];
  readonly ducks: readonly Duck[];
}

/**
 * The shadow sun, toward it: the sun's azimuth but 45 degrees up, so shadows fall about as long
 * as their casters are tall instead of nearly three times as long at the sun's 20 degrees.
 */
export function shadowSunDirection(): [number, number, number] {
  const az = (35 * Math.PI) / 180;
  const el = (45 * Math.PI) / 180;
  return [Math.cos(el) * Math.cos(az), Math.sin(el), -Math.cos(el) * Math.sin(az)];
}

/** The sun, toward it, in the world's axes: azimuth 35 and elevation 20 degrees in Blender's. */
export function sunDirection(): [number, number, number] {
  const az = (35 * Math.PI) / 180;
  const el = (20 * Math.PI) / 180;
  return [Math.cos(el) * Math.cos(az), Math.sin(el), -Math.cos(el) * Math.sin(az)];
}

/** The four sides of a tile, in the order of their wall ids: +x, minus x, +y, minus y. */
const SIDES = [
  [1, 0],
  [-1, 0],
  [0, 1],
  [0, -1],
] as const;

const TERRAIN_TONE: Record<Terrain, number> = {
  [Terrain.water]: 0.86,
  [Terrain.grass]: 1,
  [Terrain.forest]: 1,
  [Terrain.mountain]: 1,
};

/**
 * The creatures in the chasms after death (decision 0012): which one hides at a tile, and how far
 * each model sinks to peek over the rim with only its eyes and what is above them showing.
 */
export interface Creatures {
  at(world: WorldSource, x: number, y: number): string;
  readonly peeks: ReadonlyMap<string, number>;
}

/**
 * Dresses a tile instead of the placement rules: the model gallery, a creature's portrait and the
 * clearing use it. Null leaves it bare.
 */
export type Dress = (x: number, y: number) => Placement[] | null;

export class SceneBuilder {
  private readonly names: ReadonlySet<string>;
  /** `prefix-N` with N from a random number, among the variants the model set has. */
  private readonly pick: Pick;

  private dress: Dress | null;
  /** Whether a dressed world keeps the world's own gnats and fish: the clearing does. */
  private dressLife = false;
  private readonly attachments: ReadonlyMap<string, readonly Attachment[]>;
  /** Where smoke rises from each model that smokes, when the smoke is models; else empty. */
  private readonly smoke: ReadonlyMap<string, readonly SmokeSource[]>;
  private readonly creatures: Creatures | null;
  /** The flags of the motions models name for themselves, by model, riders aside. */
  private readonly motions: ReadonlyMap<string, number>;
  /** The house standing in the cabin's place: the first cabin unless the player chose another. */
  private home = HOME;
  /** The flag planted on a bannered tile: the game's own unless another was chosen. */
  private flag = BANNER_FLAG;
  /** The cairn the flag stands in on shown ground: its own where the set has one. */
  private cairn = BANNER_CAIRN;

  constructor(
    modelNames: Iterable<string>,
    dress: Dress | null = null,
    attachments: ReadonlyMap<string, readonly Attachment[]> = new Map(),
    creatures: Creatures | null = null,
    smoke: ReadonlyMap<string, readonly SmokeSource[]> = new Map(),
    motions: ReadonlyMap<string, number> = new Map(),
  ) {
    this.names = new Set(modelNames);
    this.pick = makePick(this.names);
    this.dress = dress;
    this.attachments = attachments;
    this.creatures = creatures;
    this.smoke = smoke;
    this.motions = motions;
  }

  /**
   * Dresses the scenes built from now on, or by the placement rules again with null. With `life`
   * the gnats and fish come to the dressed world by the world's own rules, as they do on a run.
   */
  setDress(dress: Dress | null, life = false): void {
    this.dress = dress;
    this.dressLife = dress !== null && life;
  }

  /**
   * The player's own cabin in the scenes built from now on (decision 0029): a house of the model
   * set in the first cabin's place, with the first cabin's flagpole and pennant beside it; the
   * first cabin again with null or a model the set lacks. The world's rules are untouched.
   */
  setHome(home: string | null): void {
    this.home = home !== null && this.names.has(home) ? home : HOME;
  }

  /** What stands in the cabin's place in the scenes it builds. */
  get homeModel(): string {
    return this.home;
  }

  /** The model picker of its model set, for what asks the placement rules itself. */
  get modelPick(): Pick {
    return this.pick;
  }

  /**
   * The flag planted on every bannered tile in the scenes built from now on: a flag model of the
   * set, built as the game's is, its pole reaching down to the mist floor; the game's own again
   * with null or a model the set lacks. The banners a supporter chooses (decision 0035). On shown
   * ground a flag stands in its own cairn where the set has one, as `iso-banner-c3-cairn` for
   * `iso-banner-c3-flag`, and in the game's cairn otherwise.
   */
  setBanner(flag: string | null): void {
    this.flag = flag !== null && this.names.has(flag) ? flag : BANNER_FLAG;
    const own = this.flag.replace(/-flag$/, "-cairn");
    this.cairn = this.names.has(own) ? own : BANNER_CAIRN;
  }

  /** The flag the scenes plant, from setBanner. */
  get bannerFlag(): string {
    return this.flag;
  }

  build(source: WorldSource, cx: number, cy: number, size: number): Scene {
    const x0 = cx - (size >> 1);
    const y0 = cy - (size >> 1);
    // The rules ask about every tile and its neighbours many times: read the window once, with a
    // margin for the farthest any rule looks round a tile.
    const world = new WindowWorld(
      source,
      x0 - WINDOW_MARGIN,
      y0 - WINDOW_MARGIN,
      size + 2 * WINDOW_MARGIN,
    );
    // Which tiles have a top, with a one-tile border, so faces toward the edge are known.
    const span = size + 2;
    const top = new Uint8Array(span * span);
    const isShown = new Uint8Array(span * span);
    for (let j = 0; j < span; j += 1) {
      for (let i = 0; i < span; i += 1) {
        const x = x0 - 1 + i;
        const y = y0 - 1 + j;
        if (!shown(world, x, y)) continue;
        isShown[j * span + i] = 1;
        if (hasTopAt(world, x, y)) top[j * span + i] = 1;
      }
    }
    const hasTop = (x: number, y: number) => top[(y - y0 + 1) * span + (x - x0 + 1)] === 1;

    const pos: number[] = [];
    const tone: number[] = [];
    const ids: number[] = [];
    const tiles: number[] = [];
    const kinds: number[] = [];
    const ground = new Uint8Array(size * size);
    const [sx, , sz] = sunDirection();
    const sideTone = (nx: number, nz: number) => {
      const l = nx * sx + nz * sz;
      return l > 0.6 ? 0.72 : l > 0.2 ? 0.62 : 0.5;
    };
    const quad = (
      a: number[],
      b: number[],
      c: number[],
      d: number[],
      t: number,
      id: number,
      i: number,
      j: number,
      k: number,
    ) => {
      for (const p of [a, b, c, a, c, d]) {
        pos.push(p[0] as number, p[1] as number, p[2] as number);
        tone.push(t);
        ids.push(id);
        tiles.push(i, j);
        kinds.push(k);
      }
    };

    const instances = new Map<string, Instance[]>();
    const place = (
      name: string,
      x: number,
      y: number,
      tx: number,
      ty: number,
      flags = 0,
      yaw = 0,
      scale = 1,
      sway: Sway = SWAY.none,
      slot = 0,
      h = 0,
      puff?: Instance["puff"],
    ) => {
      if (!this.names.has(name)) return;
      let list = instances.get(name);
      if (!list) {
        list = [];
        instances.set(name, list);
      }
      list.push({ x, y, h, tx, ty, flags, yaw, scale, sway, slot, ...(puff ? { puff } : {}) });
    };
    const markers: Marker[] = [];
    // A banner is the model where the file has it, the marker sprite otherwise. In the mist only
    // the flag stands, its pole going down into the mist; on shown ground it stands in its cairn.
    // On water it has no cairn: its pole goes down into the lake, wedged in its bed (Daniel,
    // 2026-10-26).
    const banner = (x: number, y: number, cairn: boolean) => {
      if (!this.names.has(this.flag)) {
        markers.push({ kind: "banner", x, y, n: 0 });
        return;
      }
      place(this.flag, x, y, x, y, FLAG.aloft | FLAG.face, 0, 1, SWAY.none, 30);
      if (cairn) place(this.cairn, x, y, x, y, 0, 0, 1, SWAY.none, 31);
    };
    const swarms: Gnats[] = [];
    const fishes: Fish[] = [];
    const ducks: Duck[] = [];
    const pick = (prefix: string, r: number) => this.pick(prefix, r);

    for (let j = 0; j < size; j += 1) {
      for (let i = 0; i < size; i += 1) {
        const x = x0 + i;
        const y = y0 + j;
        if (!isShown[(j + 1) * span + (i + 1)]) {
          ghostsOn(world, x, y, pick).forEach((g, slot) => {
            place(g.name, x + g.dx, y + g.dy, x, y, FLAG.ghost, g.yaw, g.scale, SWAY.none, slot);
          });
          if (world.hasBanner(x, y)) {
            banner(x, y, false);
            if (world.status === "dead" && !world.isChasm(x, y))
              markers.push({ kind: "wrong", x, y, n: 0 });
          }
          continue;
        }
        if (!hasTop(x, y)) {
          const killer = struck(world, x, y);
          // The creature that ended the run rises out of the mist, turned to the camera, its eyes
          // in the accent; the others peek over the rim, only their eyes and what is above them
          // showing. Below the rim they fade into the mist, and the gap has no floor: only mist.
          // Without the creature's model the gap shows the chasm's floor.
          const creature = this.creatures?.at(world, x, y);
          if (creature !== undefined && this.names.has(creature)) {
            place(
              creature,
              x,
              y,
              x,
              y,
              FLAG.aloft | FLAG.face | FLAG.misted | (killer ? FLAG.accent : 0),
              0,
              1,
              SWAY.none,
              1,
              killer ? 0 : -(this.creatures?.peeks.get(creature) ?? 0),
            );
            continue;
          }
          place(
            this.pick("iso-chasm", mix32(x, y, SALT_VARIANT)),
            x,
            y,
            x,
            y,
            FLAG.clear | (killer ? FLAG.accent : 0),
          );
          continue;
        }
        // The top, a wall toward every neighbour without one, and where land meets water a low
        // bank down to the water. Ids differ per tile and per face, so the grid and the plateau's
        // rim get their lines.
        const base = ((mix32(x, y, 0x69647331) % 4000) + 1) * 8;
        const terrain = world.terrain(x, y) as Terrain;
        const water = terrain === Terrain.water;
        const numbered =
          world.isRevealed(x, y) &&
          world.adjacent(x, y) > 0 &&
          !world.isClosed(x, y) &&
          !(x === 0 && y === 0);
        ground[j * size + i] = terrain + 1 + (numbered ? 8 : 0);
        const level = water ? -WATER_DROP : 0;
        const l = x - 0.5;
        const r = x + 0.5;
        const n = y - 0.5;
        const s = y + 0.5;
        quad(
          [l, level, n],
          [l, level, s],
          [r, level, s],
          [r, level, n],
          TERRAIN_TONE[terrain] ?? 1,
          base,
          i,
          j,
          water ? 1 : 0,
        );
        SIDES.forEach(([dx, dy], f) => {
          // The edge on this side, from one corner to the other.
          const ax = dx === 0 ? l : dx > 0 ? r : l;
          const bx = dx === 0 ? r : ax;
          const az = dy === 0 ? n : dy > 0 ? s : n;
          const bz = dy === 0 ? s : az;
          const face = (yTop: number, yBottom: number) =>
            quad(
              [ax, yTop, az],
              [ax, yBottom, az],
              [bx, yBottom, bz],
              [bx, yTop, bz],
              sideTone(dx, dy),
              base + 1 + f,
              i,
              j,
              0,
            );
          const nx = x + dx;
          const ny = y + dy;
          if (!hasTop(nx, ny)) face(level, -DEPTH);
          else if (!water && world.terrain(nx, ny) === Terrain.water) face(0, -WATER_DROP);
        });

        if (world.hasBanner(x, y)) banner(x, y, !water);
        if (!world.isRevealed(x, y)) continue;
        const bears = bearsObjects(world, x, y);
        const dressed = this.dress
          ? (this.dress(x, y) ?? [])
          : bears
            ? objectsOn(world, x, y, pick)
            : edgeDetailOn(world, x, y, pick);
        // The player's own cabin stands in the first cabin's place, with its flagpole.
        const own = x === 0 && y === 0 && this.home !== HOME;
        const placed = own
          ? dressed.map((p) => (p.name === HOME ? { ...p, name: this.home } : p))
          : dressed;
        // A dressed world has no gnats or fish of its own unless its dressing asks for them.
        const living = bears && (!this.dress || this.dressLife);
        const swarm = living ? swarmOn(world, x, y) : null;
        if (swarm)
          swarms.push({ x: x + swarm.dx, y: y + swarm.dy, h: swarm.h, tx: x, ty: y, n: swarm.n });
        const fish = living ? fishOn(world, x, y) : null;
        if (fish) {
          const { heading, period, phase } = fish;
          fishes.push({
            x: x + fish.dx,
            y: y + fish.dy,
            h: -WATER_DROP,
            tx: x,
            ty: y,
            heading,
            period,
            phase,
          });
        }
        placed.forEach((p, slot) => {
          const h = p.h ?? 0;
          const flags =
            ((p.span ?? 1) > 1 ? FLAG.wide : 0) |
            (this.motions.get(p.name) ?? 0) |
            (p.ghost ? FLAG.ghost : 0);
          if (flags & FLAG.dabble && !p.ghost) {
            ducks.push({ x: x + p.dx, y: y + p.dy, h, scale: p.scale, tx: x, ty: y });
          }
          place(p.name, x + p.dx, y + p.dy, x, y, flags, p.yaw, p.scale, p.sway, slot, h);
          // Riders, such as a windmill's sails, at their point on the host, turned with it.
          const c = Math.cos(p.yaw);
          const sn = Math.sin(p.yaw);
          // Smoke, when it is models: a few puffs at each source, turned and sized with the host. They
          // share one id, so the puffs of a column run together into one cloud with one outline.
          for (const src of this.smoke.get(p.name) ?? []) {
            for (let k = 0; k < PUFFS_3D; k += 1) {
              place(
                PUFF,
                x + p.dx + p.scale * (src.x * c + src.z * sn),
                y + p.dy + p.scale * (-src.x * sn + src.z * c),
                x,
                y,
                FLAG.smoke,
                0,
                1,
                SWAY.none,
                24,
                h + p.scale * src.y,
                { k, size: src.size * p.scale },
              );
            }
          }
          // A rider is a silhouette with its host, so a windmill not found yet keeps no sails of
          // its own (Daniel, 2026-10-21).
          const riders = this.attachments.get(p.name) ?? [];
          const flagpole = own && p.name === this.home ? (this.attachments.get(HOME) ?? []) : [];
          for (const a of [...riders, ...flagpole]) {
            place(
              a.name,
              x + p.dx + p.scale * (a.x * c + a.z * sn),
              y + p.dy + p.scale * (-a.x * sn + a.z * c),
              x,
              y,
              a.flags | (p.ghost ? FLAG.ghost : 0),
              p.yaw,
              p.scale,
              SWAY.none,
              slot + 16,
              h + p.scale * a.y,
            );
          }
        });
        const adjacent = world.adjacent(x, y);
        if (adjacent > 0 && !world.isClosed(x, y) && !(x === 0 && y === 0))
          markers.push({ kind: "digit", x, y, n: adjacent });
      }
    }
    return {
      x0,
      y0,
      size,
      columns: {
        position: new Float32Array(pos),
        tone: new Float32Array(tone),
        id: new Float32Array(ids),
        tile: new Float32Array(tiles),
        kind: new Float32Array(kinds),
        count: pos.length / 3,
      },
      ground,
      instances,
      markers,
      swarms,
      fish: fishes,
      ducks,
    };
  }
}
